/**
 * Variantenwahl je Konto — gemerkt am Server (`/api/preferences/variante`, server/routes/variante-wahl.ts).
 *
 * Bis 01.11.2026 lag sie nur in `sessionStorage 'cui-variant-picked'`; jedes neue Fenster und jeder
 * Neuaufbau einer Konto-Kachel fragte wieder (WIRKBELEG Partner 8a6548d7b5). localStorage ist nur
 * Zwischenspeicher, wenn der Server nicht erreichbar ist — und dann sagt die Oberflaeche das.
 */

import { httpBoundary } from '@werkingflow/trust-boundary';
import { z } from 'zod';

export interface GemerkteVariante {
  port: number | null;
  /** Woher die Wahl stammt: Server (Normalfall), lokaler Zwischenspeicher (Server nicht erreichbar) oder keine. */
  quelle: 'server' | 'lokal' | 'keine';
  /** Gesetzt, wenn der Server nicht lesbar war — die Oberflaeche zeigt ihn. */
  fehler?: string;
}

const ENDPUNKT = '/api/preferences/variante';
const LeseS = z.object({ port: z.number().int().nullable() }).passthrough();
const SchreibS = z.object({ ok: z.literal(true), port: z.number().int() }).passthrough();
/** Servertext der Fehlerantwort statt nur des Statuscodes. */
function serverFehler(status: number, text: string): { throw: Error } {
  let grund = `HTTP ${status}`;
  try { const d = JSON.parse(text) as { error?: unknown }; if (typeof d.error === 'string') grund = d.error; } catch { /* silent-ok: kein JSON, dann bleibt der Statuscode */ }
  return { throw: new Error(grund) };
}
const lokalSchluessel = (konto: string) => `cui-variante-wahl:${konto}`;

function leseLokal(konto: string): number | null {
  try {
    const roh = localStorage.getItem(lokalSchluessel(konto));
    const n = roh == null ? NaN : Number(roh);
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null; // silent-ok: localStorage gesperrt (Privatmodus) heisst nur: kein Zwischenspeicher
  }
}

function schreibeLokal(konto: string, port: number): void {
  try { localStorage.setItem(lokalSchluessel(konto), String(port)); } catch { /* silent-ok: Zwischenspeicher optional, der Server ist die Ablage */ }
}

/** Liest die Wahl dieses Kontos. Wirft nicht: ein Serverfehler kommt als `fehler` zurueck. */
export async function leseVarianteWahl(konto: string): Promise<GemerkteVariante> {
  // fallback-ok: Server nicht erreichbar -> lokaler Zwischenspeicher, der Fehler wird als Hinweis angezeigt
  try {
    const d = await httpBoundary({ name: 'cui.variante-wahl.lesen', url: ENDPUNKT, schema: LeseS, timeoutMs: 10000, onStatus: (st, t) => (st >= 400 ? serverFehler(st, t) : undefined) });
    const port = d.port;
    if (port != null) schreibeLokal(konto, port);
    return { port, quelle: port != null ? 'server' : 'keine' };
  } catch (err) {
    const lokal = leseLokal(konto);
    const grund = err instanceof Error ? err.message : String(err);
    console.warn('[varianteWahl] Server nicht lesbar, lokaler Zwischenspeicher:', grund);
    return { port: lokal, quelle: lokal != null ? 'lokal' : 'keine', fehler: `Gemerkte Variantenwahl nicht lesbar (${grund}).` };
  }
}

/** Merkt die Wahl am Server. Ergebnis `fehler` muss die Oberflaeche sichtbar machen. */
export async function merkeVarianteWahl(konto: string, port: number): Promise<{ ok: true } | { ok: false; fehler: string }> {
  schreibeLokal(konto, port);
  // fallback-ok: Speichern gescheitert -> Rueckgabe mit Fehlertext, der Aufrufer zeigt ihn sichtbar an
  try {
    await httpBoundary({ name: 'cui.variante-wahl.speichern', url: ENDPUNKT, method: 'PUT', body: { port }, schema: SchreibS, timeoutMs: 10000, onStatus: (st, t) => (st >= 400 ? serverFehler(st, t) : undefined) });
    return { ok: true };
  } catch (err) {
    const grund = err instanceof Error ? err.message : String(err);
    console.warn('[varianteWahl] Speichern gescheitert:', grund);
    return { ok: false, fehler: `Variantenwahl nicht am Konto gespeichert (${grund}). Nur in diesem Browser gemerkt.` };
  }
}
